import {ChangeEvent, DragEvent, useRef, useState} from 'react';
import {FileSpreadsheet, MailCheck, Upload, X} from 'lucide-react';
import {toast} from 'sonner';

import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import {Spinner} from '@/components/ui/spinner';
import {useUser} from '@/hooks/useUser';
import {PageHeader} from '@/layout/components/PageHeader';
import {PageWrapper} from '@/layout/components/PageWrapper';
import {cn} from '@/lib/utils';
import {useImportPhoneBookMutation} from '@/store/phoneBookImportApi';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

function formatFileSize(size: number) {
    return new Intl.NumberFormat('he-IL', {maximumFractionDigits: 1}).format(size / 1024 / 1024) + ' MB';
}

function getErrorMessage(error: unknown) {
    if (typeof error === 'object' && error && 'data' in error) {
        const data = (error as {data?: {error?: string}}).data;
        if (data?.error) return data.error;
    }
    return 'לא ניתן היה להעלות את הקובץ. נסה שוב.';
}

export default function PhoneBookImportPage() {
    const {user} = useUser();
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [queuedMessage, setQueuedMessage] = useState('');
    const [importPhoneBook, {isLoading}] = useImportPhoneBookMutation();

    if (!user?.is_superuser) {
        return (
            <PageWrapper>
                <div className="py-20 text-center text-gray-500">
                    <p className="text-xl">גישה מוגבלת לסופר יוזר בלבד</p>
                </div>
            </PageWrapper>
        );
    }

    function selectFile(selectedFile?: File) {
        if (!selectedFile) return;
        if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
            toast.error('ניתן להעלות קובץ xlsx בלבד');
            return;
        }
        if (selectedFile.size > MAX_FILE_SIZE) {
            toast.error('גודל הקובץ המרבי הוא 50 MB');
            return;
        }
        setFile(selectedFile);
        setConfirmed(false);
        setQueuedMessage('');
    }

    function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
        selectFile(event.target.files?.[0]);
        event.target.value = '';
    }

    function handleDrop(event: DragEvent<HTMLDivElement>) {
        event.preventDefault();
        setDragging(false);
        selectFile(event.dataTransfer.files?.[0]);
    }

    async function handleSubmit() {
        if (!file || !confirmed) return;
        try {
            const response = await importPhoneBook(file).unwrap();
            setQueuedMessage(response.message);
            setFile(null);
            setConfirmed(false);
            toast.success('הייבוא נוסף לתור');
        } catch (error) {
            toast.error(getErrorMessage(error));
        }
    }

    return (
        <PageWrapper className="max-w-[980px]">
            <PageHeader title="עדכון ספר טלפונים" />

            {queuedMessage ? (
                <section className="border-r-4 border-emerald-500 bg-emerald-50 px-5 py-5 text-emerald-950">
                    <div className="flex items-start gap-3">
                        <MailCheck className="mt-0.5 size-6 shrink-0 text-emerald-700" />
                        <div>
                            <h2 className="text-base font-semibold">הקובץ התקבל לעיבוד</h2>
                            <p className="mt-1 text-sm leading-6">{queuedMessage}</p>
                        </div>
                    </div>
                </section>
            ) : (
                <div className="space-y-5">
                    <section className="border border-amber-300 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-950">
                        <strong className="block text-base">הייבוא מחליף את ספר הטלפונים הקיים</strong>
                        אנשי קשר שאינם מופיעים בקובץ יימחקו. אנשי קשר עם אותו id יעודכנו, ו-id חדשים יתווספו.
                    </section>

                    <section className="border border-gray-200 bg-white p-5 shadow-sm">
                        <h2 className="text-base font-semibold text-gray-900">קובץ Excel</h2>
                        <p className="mt-1 text-sm text-gray-500">יש להעלות קובץ xlsx בפורמט הקבוע. עמודות נוספות יתקבלו ויתעלמו מהן.</p>

                        <input
                            ref={inputRef}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="hidden"
                            onChange={handleFileChange}
                        />

                        {file ? (
                            <div className="mt-5 flex min-h-24 items-center justify-between gap-4 border border-emerald-300 bg-emerald-50 px-4 py-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <FileSpreadsheet className="size-8 shrink-0 text-emerald-700" />
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
                                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" title="הסרת הקובץ" onClick={() => setFile(null)}>
                                    <X className="size-4" />
                                </Button>
                            </div>
                        ) : (
                            <div
                                className={cn(
                                    'mt-5 flex min-h-44 cursor-pointer flex-col items-center justify-center border-2 border-dashed px-5 text-center transition-colors',
                                    dragging ? 'border-light-blue-500 bg-light-blue-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400',
                                )}
                                onClick={() => inputRef.current?.click()}
                                onDragEnter={() => setDragging(true)}
                                onDragLeave={() => setDragging(false)}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <Upload className="size-8 text-gray-400" />
                                <p className="mt-3 text-sm font-medium text-gray-800">גרור לכאן קובץ או לחץ לבחירה</p>
                                <p className="mt-1 text-xs text-gray-500">xlsx בלבד, עד 50 MB</p>
                            </div>
                        )}
                    </section>

                    <label className={cn('flex items-start gap-3 text-sm leading-6', !file && 'text-gray-400')}>
                        <Checkbox
                            checked={confirmed}
                            disabled={!file || isLoading}
                            onCheckedChange={(checked) => setConfirmed(checked === true)}
                            className="mt-1"
                        />
                        אני מאשר שהקובץ הוא הרשימה המלאה ושאנשי קשר שאינם מופיעים בו יימחקו.
                    </label>

                    <div className="flex justify-end">
                        <Button disabled={!file || !confirmed || isLoading} onClick={handleSubmit} className="min-w-40">
                            {isLoading ? <><Spinner className="ml-2 size-4" />מעלה קובץ...</> : <><Upload className="ml-2 size-4" />התחלת ייבוא</>}
                        </Button>
                    </div>
                </div>
            )}
        </PageWrapper>
    );
}